import type { AxiosError } from 'axios';

type ErrorPayload = { error?: string; message?: string };

/** axios エラーのレスポンスボディ (`{ error }` / `{ message }`) を取り出す。 */
function readResponseData(err: unknown): ErrorPayload | undefined {
  return (err as AxiosError<ErrorPayload> | undefined)?.response?.data;
}

/**
 * API エラーからユーザー向けメッセージを抽出する。
 *
 * バックエンドは `{ error: '...' }` 形式で返すため、まず `response.data.error`
 * を優先する（axios の `err.message` は "Request failed with status code 403"
 * のような実情報を欠いた文字列になりがちなため）。
 *
 * 優先順位:
 *   1. err.response.data.error（バックエンドのメッセージ）
 *   2. err.message（Error インスタンス）
 *   3. fallback（既定文言）
 */
export function getErrorMessage(err: unknown, fallback = '不明なエラーが発生しました'): string {
  const backendMsg = readResponseData(err)?.error;
  if (typeof backendMsg === 'string' && backendMsg.trim()) {
    return backendMsg;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

/**
 * `response.data.error` / `response.data.message` からバックエンドのメッセージを取り出す。
 *
 * 生成系のようにメッセージ本文で分岐したい呼び出し側が、「バックエンド由来か
 * ネットワーク層 (axios) 由来か」を区別するために使う。
 */
export function getResponseErrorMessage(err: unknown): string | undefined {
  const data = readResponseData(err);
  return data?.error || data?.message || undefined;
}

/**
 * 生成ジョブ系のエラー表示に使う抽出。{@link getErrorMessage} との違い:
 *
 * - error が falsy のときは空文字（「エラーなし」を表示させないため）
 * - `response.data.message` も候補に含める
 * - Error インスタンスでなくても `message` プロパティを読む
 * - 何も取れなければ JSON.stringify にフォールバックする（原因調査のため）
 */
export function resolveErrorMessage(error: unknown): string {
  if (!error) return '';
  const responseMessage = getResponseErrorMessage(error);
  if (responseMessage) return responseMessage;
  const message = (error as { message?: string } | undefined)?.message;
  if (message) return message;
  try {
    return JSON.stringify(error);
  } catch {
    return '詳細不明のエラーが発生しました。';
  }
}
